import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Circle, Pencil, Trash2, ChevronDown, ChevronUp, Clock, BookOpen, Plus, UserCheck, UserX, UserPlus, Link } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { API } from '../../config/api';
import { useAdminSettings } from '../../hooks/useAdminSettings';
import { formatDate } from '../../utils/dateFormat';

export function CaseOverview({
  caseId,
  caseData,
  clientData,
  user,
  onRefresh,
  isStaffOrAdmin,
  onEditCase,
  onEditClient,
  onOpenReminderSettings
}) {
  const navigate = useNavigate();
  const [milestonesCollapsed, setMilestonesCollapsed] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [showCustomStatusInput, setShowCustomStatusInput] = useState(false);
  const [customStatus, setCustomStatus] = useState('');
  
  // Client linking/invitation state
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientModalMode, setClientModalMode] = useState('invite'); // 'invite' or 'link'
  const [existingClients, setExistingClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch dynamic admin settings from database
  const { 
    getStatusOptions, 
    getMilestoneOptions, 
    getMilestoneDescription, 
    getMilestoneLabel: getLabel,
    loading: settingsLoading 
  } = useAdminSettings();

  // Get dynamic data from admin settings
  const caseStatuses = getStatusOptions();
  const allMilestones = getMilestoneOptions();

  // Check if current status is a custom one (not in predefined list)
  const isCustomStatus = caseData.current_status && 
    !caseStatuses.some(s => s.value === caseData.current_status);

  // Fetch existing clients when modal opens in 'link' mode
  useEffect(() => {
    if (showClientModal && clientModalMode === 'link') {
      fetchExistingClients();
    }
  }, [showClientModal, clientModalMode]);

  const fetchExistingClients = async () => {
    try {
      const response = await fetch(`${API}/users/clients`, {
        credentials: 'include'
      });
      if (response.ok) {
        const clients = await response.json();
        setExistingClients(clients);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleOpenClientModal = (mode) => {
    setClientModalMode(mode);
    setShowClientModal(true);
    setSelectedClientId('');
    setInviteForm({ name: caseData.client_name || '', email: '', phone: '' });
  };

  const handleLinkClient = async () => {
    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('client_id', selectedClientId);
      
      const response = await fetch(`${API}/cases/${caseId}/link-client`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to link client');
      }
      
      toast.success('Client linked successfully');
      setShowClientModal(false);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInviteClient = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Name and email are required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('email', inviteForm.email);
      formData.append('name', inviteForm.name);
      if (inviteForm.phone) formData.append('phone', inviteForm.phone);
      
      const response = await fetch(`${API}/cases/${caseId}/invite-client`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send invitation');
      }
      
      const result = await response.json();
      if (result.email_delivered === false) {
        toast.error(
          `Invitation created but email FAILED: ${result.email_error || 'unknown error'}. Share this code manually: ${result.invitation_code}`,
          { duration: 15000 }
        );
      } else {
        toast.success('Invitation sent successfully');
      }
      setShowClientModal(false);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    // If "custom" is selected, show the input modal instead
    if (newStatus === '__custom__') {
      setShowCustomStatusInput(true);
      setCustomStatus('');
      return;
    }

    try {
      const response = await fetch(`${API}/cases/${caseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success('Case status updated');
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCustomStatusSubmit = async () => {
    if (!customStatus.trim()) {
      toast.error('Please enter a custom status');
      return;
    }

    // Convert to snake_case for consistency
    const formattedStatus = customStatus.trim().toLowerCase().replace(/\s+/g, '_');

    try {
      const response = await fetch(`${API}/cases/${caseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_status: formattedStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success('Custom status set successfully');
      setShowCustomStatusInput(false);
      setCustomStatus('');
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddMilestone = async () => {
    if (!selectedMilestone) {
      toast.error('Please select a milestone');
      return;
    }

    try {
      const updatedMilestones = [...(caseData.milestones || []), selectedMilestone];
      
      const response = await fetch(`${API}/cases/${caseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ milestones: updatedMilestones })
      });

      if (!response.ok) throw new Error('Failed to add milestone');
      toast.success('Milestone added successfully');
      setShowMilestoneModal(false);
      setSelectedMilestone('');
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveMilestone = async (milestoneToRemove) => {
    try {
      const updatedMilestones = (caseData.milestones || []).filter(m => m !== milestoneToRemove);
      
      const response = await fetch(`${API}/cases/${caseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ milestones: updatedMilestones })
      });

      if (!response.ok) throw new Error('Failed to remove milestone');
      toast.success('Milestone removed');
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getMilestoneLabel = (value) => {
    return getLabel(value);
  };

  // Get milestone description from dynamic data
  const getMilestoneDesc = (value) => {
    return getMilestoneDescription(value);
  };

  // Get milestones not yet added
  const availableMilestones = allMilestones.filter(
    m => !(caseData.milestones || []).includes(m.value)
  );

  return (
    <>
      <div className="space-y-6" data-testid="overview-content">
        {/* Case Information */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Case Information</h2>
            {isStaffOrAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEditCase}
                className="flex items-center gap-1"
              >
                <Pencil className="h-3 w-3" />
                Edit Case
              </Button>
            )}
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-600">Client</dt>
              <dd className="font-medium text-gray-900 flex items-center gap-2">
                <span>{caseData.client_name}</span>
                {isStaffOrAdmin && caseData.client_registered === true && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700" title="Client has registered for the portal">
                    <UserCheck className="h-3 w-3" />
                    Registered
                  </span>
                )}
                {isStaffOrAdmin && caseData.client_registered === false && (
                  <>
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="Client has not registered yet - invitation pending">
                      <UserX className="h-3 w-3" />
                      Not Registered
                    </span>
                    <button
                      onClick={() => {
                        setInviteForm({
                          name: caseData.client_name || '',
                          email: caseData.client_email || '',
                          phone: caseData.client_phone || ''
                        });
                        setClientModalMode('invite');
                        setShowClientModal(true);
                      }}
                      data-testid="send-invitation-btn"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#80298F] text-white hover:bg-[#80298F]/90 transition-colors"
                      title="Send/Resend invitation email to client"
                    >
                      <UserPlus className="h-3 w-3" />
                      Send Invitation
                    </button>
                  </>
                )}
                {isStaffOrAdmin && caseData.client_registered === null && (
                  <>
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title="No portal account linked to this client">
                      <UserX className="h-3 w-3" />
                      No Account
                    </span>
                    <button
                      onClick={() => handleOpenClientModal('invite')}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#80298F] text-white hover:bg-[#80298F]/90 transition-colors"
                      title="Invite client to portal"
                    >
                      <UserPlus className="h-3 w-3" />
                      Invite
                    </button>
                    <button
                      onClick={() => handleOpenClientModal('link')}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[#80298F] text-[#80298F] hover:bg-[#80298F]/10 transition-colors"
                      title="Link existing client account"
                    >
                      <Link className="h-3 w-3" />
                      Link
                    </button>
                  </>
                )}
                {isStaffOrAdmin && (
                  <>
                    <button
                      onClick={onEditClient}
                      className="p-1.5 text-[#80298F] hover:text-[#80298F]/80 hover:bg-[#80298F]/10 rounded-md transition-colors"
                      title="Edit client"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={onOpenReminderSettings}
                      className="p-1.5 text-[#80298F] hover:text-[#80298F]/80 hover:bg-[#80298F]/10 rounded-md transition-colors"
                      title="Client reminder settings"
                    >
                      <Clock className="h-4 w-4" />
                    </button>
                  </>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Case Type</dt>
              <dd className="font-medium text-gray-900 capitalize">{caseData.case_type.replace(/_/g, ' ')}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Created</dt>
              <dd className="font-medium text-gray-900">{formatDate(caseData.created_at)}</dd>
            </div>
            {isStaffOrAdmin && (
              <div>
                <dt className="text-sm text-gray-600">Last Updated</dt>
                <dd className="font-medium text-gray-900">{formatDate(caseData.updated_at)}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Current Status */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-semibold text-gray-900">Current Status</h2>
              <span className={`status-badge ${isCustomStatus ? 'bg-purple-100 text-purple-800' : 'active'}`}>
                {caseData.current_status.replace(/_/g, ' ')}
                {isCustomStatus && <span className="ml-1 text-xs">(custom)</span>}
              </span>
            </div>
            {user.role !== 'client' && (
              <Select 
                value={isCustomStatus ? '__custom__' : caseData.current_status} 
                onValueChange={handleStatusUpdate}
              >
                <SelectTrigger className="w-64" data-testid="status-select">
                  <SelectValue>
                    {isCustomStatus ? caseData.current_status.replace(/_/g, ' ') : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {caseStatuses.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                  <SelectItem value="__custom__" className="border-t mt-1 pt-1">
                    <span className="flex items-center text-[#80298F]">
                      <Plus className="h-4 w-4 mr-2" />
                      Enter Custom Status...
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="prose max-w-none">
            {caseData.status_description?.split('\n\n').map((paragraph, index) => (
              <p key={index} className={`text-gray-700 ${index > 0 ? 'mt-4' : ''}`}>
                {paragraph.startsWith('What Happens Next:') ? (
                  <>
                    <span className="font-semibold text-[#80298F]">What Happens Next:</span>
                    {paragraph.replace('What Happens Next:', '')}
                  </>
                ) : (
                  paragraph
                )}
              </p>
            ))}
          </div>
          
          {/* Glossary tip for clients */}
          {user.role === 'client' && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-[#80298F] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="text-gray-700">Need help understanding legal terms? </span>
                <button
                  onClick={() => navigate('/help#glossary')}
                  className="text-[#80298F] font-medium hover:underline"
                >
                  View our Legal Terms Glossary
                </button>
                <span className="text-gray-700"> on the Help page.</span>
              </div>
            </div>
          )}
        </div>

        {/* Case Milestones */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <button
            onClick={() => setMilestonesCollapsed(!milestonesCollapsed)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-[#FAA519]" />
              <h2 className="text-lg font-semibold text-gray-900">Case Milestones</h2>
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {caseData.milestones?.length || 0}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {user.role !== 'client' && (
                <Button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMilestoneModal(true);
                  }}
                  variant="outline"
                  size="sm"
                  data-testid="add-milestone-button"
                >
                  Add Milestone
                </Button>
              )}
              {milestonesCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
            </div>
          </button>

          {!milestonesCollapsed && (
            <div className="px-6 pb-6 border-t border-gray-100">
              {!caseData.milestones || caseData.milestones.length === 0 ? (
                <div className="text-center py-8">
                  <Circle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No milestones recorded yet</p>
                  {user.role !== 'client' && (
                    <p className="text-sm text-gray-400 mt-1">Add milestones to track case progress</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4 pt-4">
                  {caseData.milestones.map((milestone, index) => (
                    <div key={index} className="flex items-start space-x-4 group">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle className="h-5 w-5 text-[#FAA519]" />
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-gray-900 capitalize">
                            {getMilestoneLabel(milestone)}
                          </h4>
                          {user.role !== 'client' && (
                            <button
                              onClick={() => handleRemoveMilestone(milestone)}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove milestone"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {getMilestoneDesc(milestone) && (
                          <p className="text-sm text-gray-600 mt-2">
                            {getMilestoneDesc(milestone)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Milestone Modal */}
      <Dialog open={showMilestoneModal} onOpenChange={setShowMilestoneModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedMilestone} onValueChange={setSelectedMilestone}>
              <SelectTrigger>
                <SelectValue placeholder="Select a milestone" />
              </SelectTrigger>
              <SelectContent>
                {availableMilestones.map(milestone => (
                  <SelectItem key={milestone.value} value={milestone.value}>
                    {milestone.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMilestone && getMilestoneDesc(selectedMilestone) && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">{getMilestoneDesc(selectedMilestone)}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowMilestoneModal(false);
              setSelectedMilestone('');
            }}>Cancel</Button>
            <Button onClick={handleAddMilestone} className="bg-[#80298F] hover:bg-[#6b2278]">
              Add Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Status Dialog */}
      <Dialog open={showCustomStatusInput} onOpenChange={setShowCustomStatusInput}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Enter Custom Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Status Name
              </label>
              <Input
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
                placeholder="e.g., Awaiting Medical Records"
                data-testid="custom-status-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCustomStatusSubmit();
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter a descriptive status that best represents the current state of this case.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomStatusInput(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCustomStatusSubmit}
              className="bg-[#80298F] hover:bg-[#6b2278]"
              data-testid="save-custom-status-btn"
            >
              Set Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Invite/Link Modal */}
      <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {clientModalMode === 'invite' ? 'Invite Client to Portal' : 'Link Existing Client'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Mode Toggle */}
          <div className="flex gap-2 border-b pb-4">
            <button
              onClick={() => setClientModalMode('invite')}
              className={`flex-1 py-2 px-3 text-sm rounded-md transition-colors ${
                clientModalMode === 'invite' 
                  ? 'bg-[#80298F] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserPlus className="h-4 w-4 inline mr-2" />
              Invite New Client
            </button>
            <button
              onClick={() => setClientModalMode('link')}
              className={`flex-1 py-2 px-3 text-sm rounded-md transition-colors ${
                clientModalMode === 'link' 
                  ? 'bg-[#80298F] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Link className="h-4 w-4 inline mr-2" />
              Link Existing
            </button>
          </div>

          {clientModalMode === 'invite' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="invite-name">Client Name *</Label>
                <Input
                  id="invite-name"
                  name="client_name_new"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                  placeholder="Enter client name"
                  data-testid="invite-client-name"
                  autoComplete="new-password"
                  style={{ backgroundColor: 'white' }}
                />
              </div>
              <div>
                <Label htmlFor="invite-email">Email Address *</Label>
                <Input
                  id="invite-email"
                  name="client_email_new"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="client@example.com"
                  data-testid="invite-client-email"
                  autoComplete="new-password"
                  style={{ backgroundColor: 'white' }}
                />
              </div>
              <div>
                <Label htmlFor="invite-phone">Phone Number (optional)</Label>
                <Input
                  id="invite-phone"
                  name="client_phone_new"
                  type="tel"
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm({...inviteForm, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                  data-testid="invite-client-phone"
                  autoComplete="new-password"
                  style={{ backgroundColor: 'white' }}
                />
                <p className="text-xs text-gray-500 mt-1">If provided, an SMS invitation will also be sent.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="link-client">Select Existing Client</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger data-testid="link-client-select">
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {existingClients.map((client) => (
                      <SelectItem key={client.user_id} value={client.user_id}>
                        {client.name} ({client.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {existingClients.length === 0 && (
                  <p className="text-xs text-gray-500 mt-2">No existing clients found. Use "Invite New Client" instead.</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientModal(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={clientModalMode === 'invite' ? handleInviteClient : handleLinkClient}
              className="bg-[#80298F] hover:bg-[#6b2278]"
              disabled={isSubmitting}
              data-testid="confirm-client-action"
            >
              {isSubmitting ? 'Processing...' : (clientModalMode === 'invite' ? 'Send Invitation' : 'Link Client')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CaseOverview;
